import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

// Auto-stop after this many seconds to prevent blank/long recordings
const MAX_RECORDING_SECONDS = 30;

const VoiceInput = ({ onTranscription, targetLanguage = null, className = "", onRecordingChange = null }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const autoStopTimerRef = useRef(null);

  // Notify parent of recording state
  useEffect(() => {
    if (onRecordingChange) {
      onRecordingChange(isRecording);
    }
  }, [isRecording, onRecordingChange]);

  // Recording timer with auto-stop
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Auto-stop at max duration
          if (newTime >= MAX_RECORDING_SECONDS) {
            stopRecording();
            toast.info(`Recording auto-stopped at ${MAX_RECORDING_SECONDS} seconds`);
          }
          return newTime;
        });
      }, 1000);
      
      // Backup auto-stop timer
      autoStopTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          stopRecording();
          toast.info(`Recording auto-stopped at ${MAX_RECORDING_SECONDS} seconds`);
        }
      }, MAX_RECORDING_SECONDS * 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
      }
      setRecordingTime(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm format which is widely supported
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        if (chunksRef.current.length === 0) {
          toast.error("No audio recorded");
          return;
        }

        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        
        // Check file size (max 25MB)
        if (audioBlob.size > 25 * 1024 * 1024) {
          toast.error("Recording too long. Please record a shorter message.");
          return;
        }

        setIsProcessing(true);
        // Show typing indicator
        if (onTranscription) {
          onTranscription("", true); // Signal processing started
        }
        
        try {
          const formData = new FormData();
          formData.append("file", audioBlob, "recording.webm");
          
          // Build URL with target language if specified
          let url = "/content/voice/transcribe";
          if (targetLanguage && targetLanguage !== 'auto' && targetLanguage !== 'en') {
            url += `?target_language=${targetLanguage}`;
          }

          const response = await apiClient.post(url, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });

          if (response.data.text) {
            onTranscription(response.data.text);
            
            // Show helpful message about detected language
            if (response.data.detected_language) {
              const langName = getLanguageName(response.data.detected_language);
              if (response.data.translated_to) {
                toast.success(`Detected ${langName}, translated to ${getLanguageName(response.data.translated_to)}`);
              } else {
                toast.success(`Voice transcribed! (${langName})`);
              }
            } else {
              toast.success("Voice transcribed!");
            }
          } else {
            toast.error("Could not understand audio");
          }
        } catch (error) {
          console.error("Transcription error:", error);
          toast.error("Failed to transcribe audio");
        } finally {
          setIsProcessing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info(`Recording... Speak now (auto-stops in ${MAX_RECORDING_SECONDS}s)`);

    } catch (error) {
      console.error("Microphone error:", error);
      if (error.name === "NotAllowedError") {
        toast.error("Microphone access denied. Please allow microphone access.");
      } else {
        toast.error("Could not access microphone");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  
  // Helper to get language name from code
  const getLanguageName = (code) => {
    const languages = {
      'en': 'English', 'hi': 'Hindi', 'es': 'Spanish', 'fr': 'French',
      'de': 'German', 'pt': 'Portuguese', 'zh': 'Chinese', 'ja': 'Japanese',
      'ko': 'Korean', 'ar': 'Arabic', 'it': 'Italian', 'ru': 'Russian',
      'nl': 'Dutch', 'pl': 'Polish', 'tr': 'Turkish', 'vi': 'Vietnamese',
      'th': 'Thai', 'id': 'Indonesian', 'ur': 'Urdu', 'pa': 'Punjabi'
    };
    return languages[code?.toLowerCase()] || code;
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={isProcessing}
        className={`p-2 rounded-lg transition-all ${
          isRecording 
            ? "bg-red-500 text-white animate-pulse" 
            : isProcessing 
              ? "bg-viraly-orange/50 text-white cursor-wait"
              : "bg-white/10 hover:bg-white/20 text-viraly-muted hover:text-viraly-light"
        } ${className}`}
        title={isRecording ? "Stop recording" : targetLanguage ? `Voice input (will output in ${getLanguageName(targetLanguage)})` : "Voice input (auto-detect language)"}
        data-testid="voice-input-btn"
      >
        <AnimatePresence mode="wait">
          {isProcessing ? (
            <motion.div
              key="processing"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <Loader2 className="w-5 h-5 animate-spin" />
            </motion.div>
          ) : isRecording ? (
            <motion.div
              key="recording"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <MicOff className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <Mic className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>
      </button>
      {isRecording && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          className="flex items-center gap-2 text-xs"
        >
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-red-400 font-medium">
            {recordingTime}s / {MAX_RECORDING_SECONDS}s
          </span>
        </motion.div>
      )}
    </div>
  );
};

export default VoiceInput;
